import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  HelpCircle,
  Search,
  Book,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Lightbulb,
  ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [articles, setArticles] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState({});
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const [articlesRes, faqsRes] = await Promise.all([
        axios.get(`${API}/api/v2/help/articles`),
        axios.get(`${API}/api/v2/help/faq`)
      ]);
      setArticles(articlesRes.data.articles);
      setFaqs(faqsRes.data.faqs);
    } catch (error) {
      console.error('Failed to fetch help content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/v2/help/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaq(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Getting Started': 'bg-blue-100 text-blue-700',
      'Documents': 'bg-green-100 text-green-700',
      'Analysis': 'bg-purple-100 text-purple-700',
      'Collaboration': 'bg-orange-100 text-orange-700',
      'Products': 'bg-teal-100 text-teal-700',
      'Scoring': 'bg-amber-100 text-amber-700',
      'Export': 'bg-pink-100 text-pink-700',
      'Tracking': 'bg-indigo-100 text-indigo-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  // Article Detail View
  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-slate-50 p-8" data-testid="help-article-detail">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => setSelectedArticle(null)}
            className="mb-4"
          >
            ← Back to Help Center
          </Button>
          
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <Badge className={getCategoryColor(selectedArticle.category)}>
                {selectedArticle.category}
              </Badge>
              <CardTitle className="text-2xl mt-2">{selectedArticle.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-slate max-w-none">
                {selectedArticle.content.split('\n').map((line, i) => {
                  if (line.startsWith('## ')) {
                    return <h2 key={i} className="text-xl font-bold mt-6 mb-3">{line.replace('## ', '')}</h2>;
                  }
                  if (line.startsWith('### ')) {
                    return <h3 key={i} className="text-lg font-semibold mt-4 mb-2">{line.replace('### ', '')}</h3>;
                  }
                  if (line.startsWith('- ')) {
                    return <li key={i} className="ml-4">{line.replace('- ', '')}</li>;
                  }
                  if (line.match(/^\d+\./)) {
                    return <li key={i} className="ml-4">{line}</li>;
                  }
                  if (line.startsWith('**') && line.endsWith('**')) {
                    return <p key={i} className="font-bold">{line.replace(/\*\*/g, '')}</p>;
                  }
                  if (line.trim()) {
                    return <p key={i} className="text-slate-600 my-2">{line}</p>;
                  }
                  return null;
                })}
              </div>
              
              {selectedArticle.tags && (
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500 mb-2">Related topics:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedArticle.tags.map((tag, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="help-center">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center justify-center gap-3">
            <HelpCircle className="w-8 h-8 text-blue-600" />
            Help Center
          </h1>
          <p className="text-slate-500 mt-2">Find answers, guides, and documentation</p>
        </div>

        {/* Search */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search for help articles, FAQs, or topics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 py-6 text-lg"
                  data-testid="help-search-input"
                />
              </div>
              <Button onClick={handleSearch} className="bg-[#007AFF] px-8" data-testid="help-search-btn">
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Search Results */}
        {searchResults && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Search Results for "{searchQuery}"</CardTitle>
              <CardDescription>{searchResults.total_results} results found</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {searchResults.articles?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <Book className="w-4 h-4" /> Articles
                  </h3>
                  {searchResults.articles.map((article, i) => (
                    <div
                      key={i}
                      className="p-3 bg-slate-50 rounded-lg mb-2 cursor-pointer hover:bg-slate-100"
                      onClick={() => setSelectedArticle(article)}
                    >
                      <p className="font-medium text-slate-800">{article.title}</p>
                      <Badge className={`${getCategoryColor(article.category)} text-xs mt-1`}>
                        {article.category}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              
              {searchResults.faqs?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" /> FAQs
                  </h3>
                  {searchResults.faqs.map((faq, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg mb-2">
                      <p className="font-medium text-slate-800">{faq.question}</p>
                      <p className="text-sm text-slate-600 mt-1">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {searchResults.total_results === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Search className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No results found for "{searchQuery}"</p>
                  <p className="text-sm">Try different keywords</p>
                </div>
              )}
              
              <Button variant="ghost" onClick={() => setSearchResults(null)}>
                Clear search
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        {!searchResults && (
          <Tabs defaultValue="articles" className="space-y-4">
            <TabsList className="bg-white border w-full justify-start">
              <TabsTrigger value="articles" className="flex items-center gap-2">
                <Book className="w-4 h-4" /> User Guides
              </TabsTrigger>
              <TabsTrigger value="faq" className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> FAQ
              </TabsTrigger>
              <TabsTrigger value="tips" className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4" /> Quick Tips
              </TabsTrigger>
            </TabsList>

            {/* Articles */}
            <TabsContent value="articles">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {articles.map((article, i) => (
                  <Card 
                    key={i}
                    className="border-0 shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => setSelectedArticle(article)}
                    data-testid={`help-article-${article.id}`}
                  >
                    <CardHeader className="pb-2">
                      <Badge className={`${getCategoryColor(article.category)} w-fit`}>
                        {article.category}
                      </Badge>
                      <CardTitle className="text-lg mt-2">{article.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 line-clamp-2">
                        {article.content.substring(0, 150)}...
                      </p>
                      <Button variant="ghost" size="sm" className="mt-2 text-blue-600 p-0">
                        Read more →
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* FAQ */}
            <TabsContent value="faq">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Frequently Asked Questions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {faqs.map((faq, i) => (
                    <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleFaq(i)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50"
                        data-testid={`faq-${i}`}
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={getCategoryColor(faq.category)} variant="outline">
                            {faq.category}
                          </Badge>
                          <span className="font-medium text-slate-800">{faq.question}</span>
                        </div>
                        {expandedFaq[i] ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      {expandedFaq[i] && (
                        <div className="px-4 pb-4 pt-0 bg-slate-50">
                          <p className="text-slate-600">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Quick Tips */}
            <TabsContent value="tips">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-6">
                    <Lightbulb className="w-8 h-8 text-blue-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-2">Quick Upload</h3>
                    <p className="text-sm text-slate-600">
                      Drag and drop multiple RFP files at once for batch processing.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-md bg-gradient-to-br from-green-50 to-white">
                  <CardContent className="p-6">
                    <Lightbulb className="w-8 h-8 text-green-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-2">Tag Products</h3>
                    <p className="text-sm text-slate-600">
                      Link your products to RFP sections for better bid tracking.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-white">
                  <CardContent className="p-6">
                    <Lightbulb className="w-8 h-8 text-purple-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-2">Use @Mentions</h3>
                    <p className="text-sm text-slate-600">
                      Type @ in comments to notify team members instantly.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-white">
                  <CardContent className="p-6">
                    <Lightbulb className="w-8 h-8 text-amber-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-2">Check Bid Priority</h3>
                    <p className="text-sm text-slate-600">
                      Use the Bid Priority dashboard to focus on high-value opportunities.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-md bg-gradient-to-br from-teal-50 to-white">
                  <CardContent className="p-6">
                    <Lightbulb className="w-8 h-8 text-teal-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-2">Track Outcomes</h3>
                    <p className="text-sm text-slate-600">
                      Mark RFPs as won/lost to build your win rate analytics.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-md bg-gradient-to-br from-pink-50 to-white">
                  <CardContent className="p-6">
                    <Lightbulb className="w-8 h-8 text-pink-600 mb-3" />
                    <h3 className="font-semibold text-slate-800 mb-2">Export Reports</h3>
                    <p className="text-sm text-slate-600">
                      Export RFP analysis to PDF or Word for offline review.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default HelpCenter;
